/**
 * Types mirror the shapes `api/server.js` actually returns.
 * Where the API is loose (missing fields, unions collapsed into one object)
 * the types are deliberately loose too — lying here would just move the crash.
 */

/*
 * `user.id_role`, straight out of the `lu_role` table:
 *
 *   1 super_admin   2 admin   3 moderator   4 qr_display   5 participant
 *
 * This app used to treat 4 as "staff" and give it everything, which made the role the
 * schema calls *qr_display* the key to the certificate toggle, the accommodation roster
 * and every delegate's name. The two sets below are the split; `moderator` has no users
 * and is deliberately in neither.
 */
export const ROLE_SUPER_ADMIN = 1
export const ROLE_ADMIN = 2
export const ROLE_MODERATOR = 3
/** Runs a venue display and scans passes. Cannot reach settings, rooms or reporting. */
export const ROLE_QR_DISPLAY = 4
export const ROLE_PARTICIPANT = 5

/** Kept for the one thing it still means: "not a participant". */
export const ROLE_STAFF = ROLE_QR_DISPLAY

/** Gets the staff route tree at all. */
export const VENUE_ROLES: number[] = [ROLE_SUPER_ADMIN, ROLE_ADMIN, ROLE_QR_DISPLAY]
/** Additionally gets settings, rooms and the attendance report. */
export const ADMIN_ROLES: number[] = [ROLE_SUPER_ADMIN, ROLE_ADMIN]

export const ROLE_NAMES: Record<number, string> = {
  [ROLE_SUPER_ADMIN]: 'Super admin',
  [ROLE_ADMIN]: 'Organiser',
  [ROLE_MODERATOR]: 'Moderator',
  [ROLE_QR_DISPLAY]: 'Venue display',
  [ROLE_PARTICIPANT]: 'Participant',
}

export type Role = typeof ROLE_PARTICIPANT | typeof ROLE_STAFF | number

/** A row from `slot`, joined to `venue`, as returned by /getSlot and /qr/getSlot. */
export interface Slot {
  id: number
  /** Venue primary key, included by the staff QR-slot endpoint. */
  id_venue?: number
  slot_name: string
  /** Raw MSSQL datetime string, no zone. The API appends "+08:00" before parsing. */
  slot_begin: string
  slot_end: string
  /** Minutes before slot_end at which the check-in button unlocks. */
  offset_checkin_minute: number
  /** Slots that do not require a scan are informational (breaks, meals).
   *  Observed as a JSON boolean on the wire, but 0/1 in places — treat as truthy. */
  qr_required: number | boolean
  /** Pre-formatted by MSSQL CONVERT() in the select. */
  slot_date_short: string
  slot_date_full: string
  slot_time: string
  /** When set, check-in is open for the whole window rather than only near the end. */
  initial_register: number | boolean | null
  speaker: string | null
  venue_name: string
  /** Minutes added to slot_end. Present on /getSlot, absent on /qr/getSlot. */
  extended_time?: number | null
  /** Only selected by /qr/getSlot — this is the payload the venue screen displays. */
  qr_uuid?: string | null
  /**
   * Only on /qr/getSlot. False (the default) means the screen draws a rotating code
   * and the bare qr_uuid is refused; true means the stored uuid is shown and accepted.
   */
  static_qr?: number | boolean | null

  /** Computed server-side in /getSlot: is the check-in button live right now. */
  show_btn?: boolean
  /** Computed server-side in /getSlot: has this user already scanned this slot. */
  checkin_status?: boolean
}

export interface Session {
  email: string
  name: string
  /** The JWT. Valid only while it is a member of the Redis set `user:<email>:tokens`. */
  at: string
  img: string | null
  track_name: string
  track_id: number | null
  verified_name: boolean
  id_role: Role
}

/** /validateGoogle, /altLogin and /checkOTP all return this same envelope. */
export interface LoginResponse {
  auth: boolean
  email?: string
  name?: string
  at?: string
  img?: string | null
  track_name?: string
  track_id?: number | null
  verified_name?: boolean
  id_role?: Role
  /** /validateGoogle only: the email exists in `user` but may not be on this event. */
  registered_user?: boolean
  event_registered?: boolean
  /** /checkOTP only: false means the code was wrong or expired. */
  otp?: boolean
}

export interface GetSlotResponse {
  onGoing: Slot[]
  slots: Slot[]
  total_checkin: number
}

export interface QrGetSlotResponse {
  onGoing: Slot[]
  slots: Slot[]
}

export interface TrackOption {
  id: number
  track_name: string
}

export interface ParticipantProfile {
  full_name: string
  verified_name: boolean
  /** The name may no longer change: certificate issued, or the organiser froze names. */
  name_locked: boolean
  track_id: number | null
  track_name: string
  tracks: TrackOption[]
}

export interface SaveProfileResponse {
  is_success: boolean
  full_name: string
  verified_name: boolean
  track_id: number
  track_name: string
}

/**
 * `assigned` covers both "has a room" and "waiting for one" — the room number is what
 * separates those. `own_arrangement` is a delegate who does not need a room from us at
 * all, typically because they are bringing family and booking their own. The database
 * refuses a row that is `own_arrangement` and holds a room, so the two never overlap.
 */
export type AccommodationStatus = 'assigned' | 'own_arrangement'

export interface AccommodationAssignment {
  email: string
  name: string
  room_number: string | null
  status: AccommodationStatus
  /** When the key was handed over at the hotel desk. Null until an organiser ticks it. */
  key_collected_at: string | null
}

export interface AccommodationResponse {
  room_number: string | null
  status: AccommodationStatus
  key_collected_at: string | null
}

export type AccommodationFilter = 'all' | 'assigned' | 'unassigned' | 'own' | 'nokey'

export interface AccommodationCounts {
  total: number
  assigned: number
  /** Not outstanding work — these people told us they do not need a room. */
  own_arrangement: number
  /** Still needs a room from us. `assigned + own_arrangement + unassigned === total`. */
  unassigned: number
  /** Of `assigned`, how many have collected their key. */
  keys_collected: number
}

export interface AccommodationParticipantsResponse {
  participants: AccommodationAssignment[]
  /** Rows matching the current query and filter. Larger than `participants.length` when the page is capped. */
  total: number
  limit: number
  offset: number
  /**
   * Event-wide progress, deliberately unaffected by the query or filter — the header
   * figure must not move while the organiser is typing.
   */
  counts: AccommodationCounts
}

export interface AccommodationImportItem {
  participant_email: string
  room_number: string
}

export interface AccommodationImportIssue {
  line: number
  participant_email?: string
  reason: string
}

export interface AccommodationImportResponse {
  ok: boolean
  imported: number
  updated: number
  skipped: number
  issues: AccommodationImportIssue[]
}

/**
 * Per-event settings an organiser controls. The two links are null until someone sets
 * them, which is what keeps the Gallery and Resources screens on their "coming soon"
 * state rather than offering a link to nowhere.
 */
export interface EventSettings {
  show_cert: boolean
  gallery_url: string | null
  resources_url: string | null
  /** Acoustic check-in. Off by default; a missing row reads as off. */
  sound_checkin: boolean
  /** Organiser freeze on certificate names, event-wide. Set before the PDFs run. */
  names_locked: boolean
  /** Whether a UiTM attendance form is set. The address itself comes from /attendance/link. */
  attendance_link: boolean
  /** The address, only present for organisers, to edit on Settings. */
  attendance_url?: string | null
  /** Organiser switch for the tile. Only present for organisers. */
  attendance_on?: boolean
  /*
   * Percent, never the fraction the column holds. `setting_apps.percent_show` stores
   * 0.7; the API converts on the way out and back, so nothing on this side ever handles
   * a number that would render as "1%".
   *
   * Null means the event has no settings row at all — which the app reads as 100, so
   * nobody is reported eligible on the strength of a missing row. Saving anything here
   * creates the row.
   */
  certificate_threshold: number | null
}

export interface CertStatus {
  show_cert: boolean
  cert_eligable?: boolean
  /* Committee track: eligible without scanning. The screens say so instead of 0 of N. */
  attendance_exempt?: boolean
  feedback_done?: number | boolean | null
  cert_url: string | null
}

export interface OtpRequestResponse {
  user_status: boolean
  role?: Role
  message?: string
}

export interface CheckInResponse {
  msg: string
}

/** The rotating QR payload for one slot: "cpd1:" plus the current signed code. */
export interface QrCodeResponse {
  ok: boolean
  id_slot: number
  static_qr: boolean
  payload: string
  expires_in_ms: number
  window_ms: number
}

/** attended/expected for one slot, for the steward in that room. */
export interface VenueAttendanceResponse {
  ok: boolean
  id_slot: number
  attended: number
  expected: number
}

/** An organiser recorded a check-in on a delegate's behalf. */
export interface ManualCheckInResponse {
  ok: boolean
  id_slot: number
  participant_email: string
  duplicate: boolean
}

/** The rotating code `/venue/show` broadcasts over the room speakers. */
export interface SoundCodeResponse {
  ok: boolean
  id_slot: number
  code: string
  /** How long this code stays valid; fetch the next one just before it lapses. */
  expires_in_ms: number
  window_ms: number
  /** How long the broadcast lease lasts without another call. */
  lease_ms?: number
  /** ggwave protocol name to transmit with; the listener decodes any. */
  protocol?: string
}

/* ------------------------------------------------------------- delegate pass */

export interface PassIssued {
  ok: boolean
  /** A short-lived signed token. Rendered as the QR and never stored. */
  pass: string
  /** Seconds the server minted it for; the client refreshes well inside this. */
  expires_in: number
  name: string
}

/**
 * The answer at a door.
 *
 * `valid: false` is a normal 200, not an error: at a door "expired" and "the network is
 * down" must not look the same to whoever is holding the scanner.
 */
export interface PassVerdict {
  ok: boolean
  valid: boolean
  reason?: 'expired' | 'invalid' | 'not_a_pass' | 'wrong_event' | 'no_account'
  name?: string
  email?: string
  track_name?: string | null
  /** Whether they hold a participant row for this event, not merely an account. */
  registered?: boolean
}

/**
 * The answer from /pass/checkin: a PassVerdict plus what happened to the ledger.
 * `recorded: false` with `not_on_track` is still `valid: true` - the pass is fine, the
 * person is just not on a track that carries this session.
 */
export interface PassCheckinVerdict extends Omit<PassVerdict, 'reason'> {
  reason?: PassVerdict['reason'] | 'not_on_track'
  id_slot?: number
  slot_name?: string | null
  recorded?: boolean
  duplicate?: boolean
}

/** Reply from /slot/extend. `extended_time` is read back from the row, not echoed. */
export interface ExtendSlotResponse {
  ok: boolean
  id_slot: number
  extended_time: number
}

/** One row of /admin/attendance/summary — a session and how many of its cohort came. */
export interface AttendanceSummarySlot {
  id: number
  slot_name: string
  venue_name: string | null
  slot_date: string
  slot_time: string
  qr_required: boolean
  attended: number
  /**
   * Participants whose track carries this slot, status 1. Not the event headcount:
   * a five-track event has five different denominators.
   */
  expected: number
}

export interface AttendanceSummaryResponse {
  ok: boolean
  slots: AttendanceSummarySlot[]
}

/** One row of /admin/attendance/detail — one person, one session. */
export interface AttendanceDetailRow {
  id_slot: number
  slot_name: string
  venue_name: string | null
  slot_date: string
  slot_time: string
  name: string | null
  email: string
  track_name: string | null
  /** 'YYYY-MM-DD HH:mm:ss', Malaysian wall clock, formatted by the server. */
  checked_in_at: string
}

export interface AttendanceDetailResponse {
  ok: boolean
  rows: AttendanceDetailRow[]
}

/** A staff account as /admin/users returns it. */
export interface AdminUser {
  id: number
  user_name: string
  full_name: string | null
  id_role: number
  role_name: string | null
  status_active: boolean
  /** False for `supadmin` today, which is why it cannot sign in at all. */
  has_password: boolean
  last_login: string | null
  /** `id_event` is null when the venue row is gone; see the outside-this-event group. */
  venues: Array<{ id: number; name: string | null; id_event: number | null }>
}

export interface AdminUsersResponse {
  ok: boolean
  users: AdminUser[]
  venues: Array<{ id: number; name: string }>
  /** qr_venue rows whose account no longer exists. Surfaced, not hidden. */
  orphan_venue_rows: Array<{ user_name: string; id_venue: number; venue_name: string | null }>
}

/** Password replies carry the plaintext exactly once; nothing stores it. */
export interface AdminPasswordResponse {
  ok: boolean
  id: number
  user_name: string
  password: string
}

export interface AdminCreateUserResponse extends AdminPasswordResponse {
  id_role: number
}

/** An event record, with what is attached to it. */
export interface AdminEvent {
  id: number
  event_name: string
  short_name: string | null
  organizer: string | null
  /** `event.is_current` — the one the app serves. Enforced unique by a filtered index. */
  is_current: boolean
  venue: string | null
  /** YYYY-MM-DD. The columns are datetime2; the time of day is not edited here. */
  event_start: string
  event_end: string
  status: number
  counts: { slots: number; tracks: number; venues: number; participants: number }
}

export interface AdminVenue {
  id: number
  venue_name: string | null
  id_event: number | null
  main_venue: boolean
}

export interface AdminEventsResponse {
  ok: boolean
  /** The event this build serves, echoed back so the screen can mark it. */
  live_id_event: number | null
  events: AdminEvent[]
  venues: AdminVenue[]
}

/* ------------------------------------------------------------ certificates */

/** Where the name goes: PDF points, origin top-left of page 1. */
export interface CertificateLayout {
  x: number
  y: number
  width: number
  font_size: number
  align: 'left' | 'center' | 'right'
  font: string
  /* How the name is cased on the print; the stored name is untouched. */
  text_case: 'as_typed' | 'upper' | 'title'
}

export interface CertificateState {
  ok: boolean
  has_template: boolean
  page: { width: number; height: number; pages: number } | null
  layout: CertificateLayout | null
  fonts: string[]
  counts: { participants: number; eligible: number; issued: number; pending: number }
  sample_names: string[]
}

/** One row of the per-person certificate section: standing plus, if issued, the certificate. */
export interface CertificatePerson {
  user_name: string
  full_name: string | null
  cert_no: string | null
  cert_url: string | null
  dt_cert_gen: string | null
  eligible: boolean
  /** Committee track: eligible without scanning. */
  exempt: boolean
  have: number
  need: number
}

export interface IssuedCertificate {
  user_name: string
  full_name: string | null
  cert_no: string
  cert_url: string
  dt_cert_gen: string | null
}

export interface CertificateGenerateResponse {
  ok: boolean
  generated: number
  failed: string[]
  skipped_issued: number
  not_eligible: number
}

/* -------------------------------------------------------------- idea board */

export type IdeaCategory = 'feature' | 'improvement' | 'bug' | 'other'
export type IdeaModule = 'registration' | 'consultation' | 'pharmacy' | 'laboratory' | 'xray' | 'nursing' | 'reports' | 'others'
export type IdeaStatus = 'pending' | 'approved' | 'planned' | 'done' | 'declined' | 'hidden'

export interface Idea {
  id: number
  /** Poster's display name. Delegates see this; the address is organiser-only. */
  name: string
  email?: string
  mine: boolean
  category: IdeaCategory
  /** Which part of CLiS it is about. */
  module: IdeaModule
  body: string
  status: IdeaStatus
  pinned: boolean
  created_at: string
  votes: number
  voted: boolean
}

/* ----------------------------------------------------------- announcements */

export type AnnouncementLevel = 'info' | 'urgent'

export interface Announcement {
  id: number
  body: string
  level: AnnouncementLevel
  active: boolean
  created_at: string
  expires_at: string | null
  /** Organiser view only. */
  created_by?: string
}
